'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { Server, Globe, Trophy, Wrench } from 'lucide-react'
import { FglHeader } from '@/components/layout/fgl-header'
import { useTranslation } from '@/i18n/client'
import { LenisProvider,
  Reveal,
  Magnetic,
  LivingThermal,
  GliderBackdrop } from '@/components/award'
import { DonorBadge } from '@/components/ui/donor-badge'
import { DonorAvatar, type DonorTier } from '@/components/ui/donor-avatar'

// R91.7 (2026-05-19): Donate-Email + Plan-IDs werden zur Laufzeit aus dem
// Admin-Panel (system_configs via /api/public/paypal-config) gelesen.
// Default-Fallback wenn die API noch nichts geliefert hat.
const DEFAULT_DONATE_EMAIL = 'info@modellpilot.eu'

interface PayPalPublicConfig {
  donateEmail: string
  planSupporterMonthly: string | null
  planGoennerMonthly: string | null
  mode: 'sandbox' | 'live'
}

function buildDonateUrl(
  amount: number | undefined,
  isSubscription: boolean,
  cfg: PayPalPublicConfig,
): string {
  // R91.4: Alle Betraege fix. PayPal lockt den Betrag auf den jeweiligen
  // Tier-Wert. Wer mehr geben moechte, waehlt die naechste Stufe (oder den
  // "Freier Betrag"-Button unter dem Grid).
  // R91.6: Item-Namen mit echten Umlauten — PayPal kodiert via UTF-8 sauber.
  // R91.7: Plan-IDs + Donate-Email kommen jetzt aus dem Admin-Panel.
  const email = cfg.donateEmail || DEFAULT_DONATE_EMAIL
  if (isSubscription && amount) {
    const planId = amount === 8
      ? cfg.planSupporterMonthly
      : amount === 12
        ? cfg.planGoennerMonthly
        : null
    if (planId) {
      return `https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=${planId}`
    }
    // Fallback (Plan-ID noch nicht im Admin-Panel hinterlegt) — Donate-Link
    // mit fixem Betrag und Item-Name als Hinweis dass es um monatlich geht.
    const item = encodeURIComponent(`FGL Förderbeitrag ${amount} € monatlich`)
    return `https://www.paypal.com/donate?business=${encodeURIComponent(email)}&currency_code=EUR&amount=${amount}&item_name=${item}`
  }
  // Einmalzahlung
  const item = amount
    ? encodeURIComponent(`FGL Trinkgeld / Förderbeitrag ${amount} €`)
    : encodeURIComponent(`FGL Trinkgeld / Förderbeitrag`)
  const base = `https://www.paypal.com/donate?business=${encodeURIComponent(email)}&currency_code=EUR&item_name=${item}`
  return amount ? `${base}&amount=${amount}` : base
}

export default function DonatePage() {
  const { t } = useTranslation()
  const { data: session, status: sessionStatus } = useSession()

  // R91.2 follow-up: eingeloggte User sehen ihr eigenes Profilbild in ALLEN
  // 5 Badges (statt der 5 Beispiel-Modellpiloten). Macht Live-Vorschau
  // konkret — der Pilot sieht direkt wie jeder Ring um SEIN Bild wirkt.
  //
  // R91.6 (2026-05-19): Bug-Fix — session.user.image ist meist leer (NextAuth
  // hat nur OAuth-Bilder), das echte Profilbild liegt in pilot_profile
  // (avatar_media_id-Feld haelt eine URL). Daher hier per Effect aus
  // /api/pilot-profile nachziehen.
  const [userImage, setUserImage] = useState<string | null>(null)
  const userName = session?.user?.name ?? null

  // R91.7 (2026-05-19): Donate-Email + Plan-IDs zur Laufzeit aus
  // /api/public/paypal-config holen — Admin kann das im /admin/paypal-Panel
  // pflegen ohne dass die App neu gebaut werden muss.
  const [paypalCfg, setPaypalCfg] = useState<PayPalPublicConfig>({
    donateEmail: DEFAULT_DONATE_EMAIL,
    planSupporterMonthly: null,
    planGoennerMonthly: null,
    mode: 'sandbox',
  })

  useEffect(() => {
    fetch('/api/public/paypal-config')
      .then((r) => r.json())
      .then((cfg) => {
        if (cfg && typeof cfg === 'object') {
          setPaypalCfg({
            donateEmail: cfg.donateEmail || DEFAULT_DONATE_EMAIL,
            planSupporterMonthly: cfg.planSupporterMonthly ?? null,
            planGoennerMonthly: cfg.planGoennerMonthly ?? null,
            mode: cfg.mode === 'live' ? 'live' : 'sandbox',
          })
        }
      })
      .catch(() => { /* Default-Fallback bleibt */ })
  }, [])

  const donateUrl = (amount?: number, isSubscription = false) =>
    buildDonateUrl(amount, isSubscription, paypalCfg)

  // r19.48 (2026-06-29): Einmal-Spenden gehen jetzt über PayPal Orders v2
  // (Server-side createOrder + approvalUrl-Redirect), damit Backend nach
  // Capture automatisch DonationService.recordPayment() ruft → Ring +
  // Tier-Update + Zuwendungsbestätigung passieren ohne manuelle Erfassung.
  // Subscriptions bleiben weiter Direkt-Link zum PayPal-Subscribe-Plan.
  const [startingDonate, setStartingDonate] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)

  const startDonate = async (amountEur: number, tier?: string) => {
    if (startingDonate) return
    setStartingDonate(true)
    setStartError(null)
    try {
      const res = await fetch('/api/donate/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amountCents: Math.round(amountEur * 100),
          tier: tier || null,
        }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j?.approvalUrl) {
        setStartError(j?.error || `Fehler ${res.status}`)
        setStartingDonate(false)
        return
      }
      // Direktes Redirect zur PayPal-Approval-Page
      // r19.55.202: PayPal in neuem Tab statt Same-Tab-Redirect,
      // damit der User FGL nicht verliert und nach Zahlung bequem zurueckwechseln kann.
      window.open(j.approvalUrl as string, '_blank', 'noopener,noreferrer')
    } catch (err: any) {
      setStartError(err?.message || 'Netzwerk-Fehler')
      setStartingDonate(false)
    }
  }

  // r19.48.1 (2026-06-29): Eigenes Modal statt window.prompt - passend zum
  // Award-Glass-Design der /donate-Seite.
  const [freeAmountOpen, setFreeAmountOpen] = useState(false)
  const [freeAmountValue, setFreeAmountValue] = useState('25')
  const [freeAmountError, setFreeAmountError] = useState<string | null>(null)

  const startFreeAmount = () => {
    setFreeAmountValue('25')
    setFreeAmountError(null)
    setFreeAmountOpen(true)
  }

  const confirmFreeAmount = async () => {
    const v = Number(freeAmountValue.replace(',', '.'))
    if (!Number.isFinite(v) || v < 1 || v > 10000) {
      setFreeAmountError('Bitte einen Betrag zwischen 1 und 10.000 EUR eingeben.')
      return
    }
    setFreeAmountOpen(false)
    await startDonate(v, 'FREE_AMOUNT')
  }

  // Success-Banner Detection — PayPal redirect mit ?status=success&amount=X&invoice=Y
  const [successInfo, setSuccessInfo] = useState<{ amount: string; invoice: string } | null>(null)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams(window.location.search)
    if (sp.get('status') === 'success') {
      setSuccessInfo({
        amount: sp.get('amount') || '',
        invoice: sp.get('invoice') || '',
      })
      // URL bereinigen damit Refresh nicht erneut den Banner zeigt
      window.history.replaceState({}, '', '/donate')
    } else if (sp.get('status') === 'error') {
      setStartError(sp.get('msg') || 'PayPal-Zahlung fehlgeschlagen')
      window.history.replaceState({}, '', '/donate')
    } else if (sp.get('status') === 'cancelled') {
      setStartError('Du hast die PayPal-Zahlung abgebrochen. Wenn du es nochmal versuchen möchtest, einfach unten den gewünschten Betrag wählen.')
      window.history.replaceState({}, '', '/donate')
    }
  }, [])

  useEffect(() => {
    if (sessionStatus !== 'authenticated') {
      setUserImage(null)
      return
    }
    // Sofort Fallback auf NextAuth-Image (falls vorhanden), waehrend Profil laedt
    setUserImage(session?.user?.image ?? null)
    fetch('/api/pilot-profile')
      .then((r) => r.json())
      .then((j) => {
        const url = j?.data?.avatarMediaId as string | null | undefined
        if (url) setUserImage(url)
      })
      .catch(() => {
        // Stiller Fallback — image bleibt was NextAuth liefert (oft null)
      })
  }, [sessionStatus, session?.user?.image])

  // R91 (2026-05-19): 5-Tier-Donor-System mit Beispiel-Badges
  // Stephan-Spec: Beispiele direkt sichtbar auf den Badges.
  const TIER_OPTIONS: Array<{
    tier: Exclude<DonorTier, 'NONE' | 'SUPPORTER_BLUE' | 'GOENNER_ONETIME'>
    paypalAmount?: number
    isSubscription?: boolean
  }> = [
    { tier: 'SUPPORTER',         paypalAmount: 10 },
    { tier: 'UNTERSTUETZER',     paypalAmount: 20 },
    { tier: 'FOERDERER',         paypalAmount: 50 },
    { tier: 'SUPPORTER_MONTHLY', paypalAmount: 8,  isSubscription: true },
    { tier: 'GOENNER_MONTHLY',   paypalAmount: 12, isSubscription: true },
  ]

  const TRANSPARENZ = [
    { icon: Server, code: '01', titleKey: 'donate.transp01Title', descKey: 'donate.transp01Desc' },
    { icon: Globe, code: '02', titleKey: 'donate.transp02Title', descKey: 'donate.transp02Desc' },
    { icon: Wrench, code: '03', titleKey: 'donate.transp03Title', descKey: 'donate.transp03Desc' },
    { icon: Trophy, code: '04', titleKey: 'donate.transp04Title', descKey: 'donate.transp04Desc' },
  ]

  return (
    <LenisProvider>
      <div data-award="true" className="relative min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
        <FglHeader />
      <GliderBackdrop />

        {/* r19.48 (2026-06-29): Success/Error-Banner nach PayPal-Return */}
        {successInfo && (
          <div className="relative z-30 pt-[80px] md:pt-[100px] px-6">
            <div className="max-w-3xl mx-auto rounded-2xl p-5 text-center" style={{ background: 'color-mix(in oklch, var(--accent) 16%, transparent)', border: '1px solid var(--accent)' }}>
              <div className="text-[11px] font-black uppercase tracking-[.22em] mb-1" style={{ color: 'var(--accent)' }}>
                Danke für deine Spende!
              </div>
              <div className="text-base md:text-lg font-bold" style={{ color: 'var(--text)' }}>
                {successInfo.amount} EUR — wir freuen uns sehr 🙏
              </div>
              {successInfo.invoice && (
                <div className="text-xs mt-2 text-[var(--muted)]">
                  Zuwendungsbestätigung <strong>{successInfo.invoice}</strong> wird per E-Mail verschickt. Der goldene Ring erscheint binnen weniger Sekunden auf deinem Profil — eventuell Seite neu laden.
                </div>
              )}
            </div>
          </div>
        )}
        {startError && (
          <div className="relative z-30 pt-[80px] md:pt-[100px] px-6">
            <div className="max-w-3xl mx-auto rounded-2xl p-5 text-center" style={{ background: 'color-mix(in oklch, #ef4444 16%, transparent)', border: '1px solid #ef4444' }}>
              <div className="text-[11px] font-black uppercase tracking-[.22em] mb-1" style={{ color: '#fca5a5' }}>
                PayPal-Hinweis
              </div>
              <div className="text-sm" style={{ color: 'var(--text)' }}>
                {startError}
              </div>
            </div>
          </div>
        )}

        {/* r19.48.1 (2026-06-29): Eigenes Award-Modal für "Freier Betrag" statt window.prompt */}
        {freeAmountOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)' }}
            onClick={() => setFreeAmountOpen(false)}
          >
            <div
              className="relative w-full max-w-md rounded-2xl p-6 md:p-8"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--accent)',
                boxShadow: '0 0 40px color-mix(in oklch, var(--accent) 35%, transparent), 0 24px 56px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[11px] font-black uppercase tracking-[.22em] mb-2" style={{ color: 'var(--accent)' }}>
                {t('donate.freeAmountModalEyebrow')}
              </div>
              <h3
                className="font-black uppercase mb-3"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(22px, 3vw, 28px)',
                  color: 'var(--text)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.1,
                }}
              >
                {t('donate.freeAmountModalTitle')}
              </h3>
              <p
                className="text-sm text-[var(--muted)] leading-relaxed mb-5"
                dangerouslySetInnerHTML={{ __html: t('donate.freeAmountModalBody') }}
              />

              <label className="block mb-4">
                <span className="text-[10px] font-black uppercase tracking-[.18em] text-[var(--muted)] block mb-2">
                  {t('donate.freeAmountModalInputLabel')}
                </span>
                <div className="relative">
                  <input
                    type="number"
                    inputMode="decimal"
                    min="1"
                    max="10000"
                    step="1"
                    value={freeAmountValue}
                    autoFocus
                    onChange={(e) => {
                      setFreeAmountValue(e.target.value)
                      setFreeAmountError(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void confirmFreeAmount()
                      if (e.key === 'Escape') setFreeAmountOpen(false)
                    }}
                    className="w-full rounded-xl px-5 py-4 text-2xl font-black tabular-nums pr-14"
                    style={{
                      background: 'color-mix(in oklch, var(--bg) 60%, transparent)',
                      border: `1px solid ${freeAmountError ? '#ef4444' : 'var(--border)'}`,
                      color: 'var(--text)',
                      outline: 'none',
                    }}
                  />
                  <span
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-xl font-black"
                    style={{ color: 'var(--accent)' }}
                  >
                    €
                  </span>
                </div>
                {freeAmountError && (
                  <span className="text-xs mt-2 block" style={{ color: '#fca5a5' }}>
                    {freeAmountError}
                  </span>
                )}
              </label>

              {/* Quick-Picks */}
              <div className="flex gap-2 mb-6 flex-wrap">
                {[5, 25, 50, 100, 250].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => { setFreeAmountValue(String(q)); setFreeAmountError(null) }}
                    className="px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider transition-all"
                    style={{
                      background: freeAmountValue === String(q) ? 'var(--accent)' : 'transparent',
                      color: freeAmountValue === String(q) ? 'var(--bg)' : 'var(--accent)',
                      border: '1px solid var(--accent)',
                    }}
                  >
                    {q} €
                  </button>
                ))}
              </div>

              <div className="flex gap-3 justify-end flex-wrap">
                <button
                  type="button"
                  onClick={() => setFreeAmountOpen(false)}
                  className="award-btn award-btn-ghost"
                >
                  {t('donate.freeAmountModalCancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void confirmFreeAmount()}
                  disabled={startingDonate}
                  className="award-btn award-btn-gold inline-flex items-center gap-2"
                  style={{ opacity: startingDonate ? 0.5 : 1, cursor: startingDonate ? 'wait' : 'pointer' }}
                >
                  {t('donate.freeAmountModalSubmit')} <span aria-hidden>→</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HERO mit lebendiger Aurora-Atmosphaere */}
        <section className="relative pt-[100px] pb-10 md:pt-[120px] md:pb-14 overflow-hidden">
          <LivingThermal />
          <div className="absolute inset-0 award-noise pointer-events-none" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="award-eyebrow">{t('donate.pageEyebrow')}</span>
                <span className="h-px w-16 bg-[var(--award-line-hi)]" />
                <span className="award-label">{t('donate.pageEyebrowSub')}</span>
              </div>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>
                  <h1 className="award-h-mega">
                    {t('donate.heroTitle1')} <span className="award-grad-text">{t('donate.heroTitleAccent')}</span>
                  </h1>
                </Reveal>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Reveal delay={2}>
                  <p className="award-body-lead">
                    {t('donate.heroLead')}
                  </p>
                </Reveal>
                <Reveal delay={3}>
                  <div className="mt-6 flex flex-wrap gap-2">
                    <span className="award-chip">{t('donate.chipNoSubscription')}</span>
                    <span className="award-chip">{t('donate.chipNoObligation')}</span>
                    <span className="award-chip">{t('donate.chipGoldenRing')}</span>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* DONATE-OPTIONEN — Anker für Sprung "Wähle jetzt" vom Ring-Block weiter unten */}
        <section
          id="auswahl"
          className="relative py-16 md:py-24 border-y scroll-mt-24"
          style={{ background: 'var(--award-ink-2)', borderColor: 'var(--award-line)' }}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-end justify-between mb-10">
                <div>
                  <div className="award-eyebrow mb-2">PayPal · {paypalCfg.donateEmail}</div>
                  <h2 className="award-h-md">{t('donate.optionsHeadingTip')} <span className="award-grad-text">{t('donate.optionsHeadingTipSuffix')}</span></h2>
                  <p className="text-sm mt-2 max-w-2xl" style={{ color: 'var(--award-cirrus-3)' }}>
                    {t('donate.optionsDescription')}
                  </p>
                </div>
                <span className="award-label hidden md:block">{t('donate.opensInNewTab')}</span>
              </div>
            </Reveal>

            {/* R91.6 (2026-05-19): zurueck auf einreihiges 5-spaltiges Layout
                (zuvor 3-col mit ugly Leerzelle in der Mitte). Reihenfolge so
                gewaehlt dass Paare nebeneinander stehen:
                  Supporter | Supporter Monthly | Unterstuetzer | Foerderer | Goenner Monthly
                items-stretch + h-full innen sorgt fuer gleiche Card-Hoehe.
                Reveal-Wrapper EINMAL ums Grid (kein Stagger pro Card). */}
            <Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 items-stretch">
                {(['SUPPORTER', 'SUPPORTER_MONTHLY', 'UNTERSTUETZER', 'FOERDERER', 'GOENNER_MONTHLY'] as const).map((tier) => {
                  const opt = TIER_OPTIONS.find((t) => t.tier === tier)!
                  return (
                    <div key={tier} className="h-full flex">
                      <DonorBadge
                        tier={tier}
                        avatarSize={88}
                        exampleName="FGL"
                        userImage={userImage}
                        userName={userName}
                        className="flex-1"
                        onAction={() => {
                          if (!opt.paypalAmount) return
                          // Subscriptions weiter via Direkt-Link (PayPal-Plan-Page)
                          // r19.48: Einmalzahlungen via Server-Side createOrder
                          if (opt.isSubscription) {
                            window.open(
                              donateUrl(opt.paypalAmount, true),
                              '_blank',
                              'noopener,noreferrer',
                            )
                          } else {
                            void startDonate(opt.paypalAmount, opt.tier)
                          }
                        }}
                      />
                    </div>
                  )
                })}
              </div>
            </Reveal>

            {/* R91.4 (2026-05-19): Hinweis — alle Betraege sind fix auf den
                Stufen-Wert gelockt. Wer mehr geben moechte, waehlt die
                naechste Stufe. PayPal laesst keine Erhoehung zu.
                R91.5: Plus "Freier Betrag"-Button als Ausweg fuer Highroller
                und alle die zwischen den Stufen liegen. */}
            <Reveal delay={2}>
              <div className="mt-6 mx-auto max-w-3xl text-center space-y-3">
                <p className="text-[12px]" style={{ color: 'var(--award-cirrus-3)' }} dangerouslySetInnerHTML={{ __html: t('donate.amountsLockedNote') }} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }} dangerouslySetInnerHTML={{ __html: t('donate.amountsCustomNote') }} />
                <div className="pt-1">
                  <Magnetic>
                    <button
                      type="button"
                      onClick={() => void startFreeAmount()}
                      disabled={startingDonate}
                      className="award-btn award-btn-ghost inline-flex items-center gap-2"
                      style={{ opacity: startingDonate ? 0.5 : 1, cursor: startingDonate ? 'wait' : 'pointer' }}
                    >
                      {t('donate.freeAmountButton')} <span aria-hidden>→</span>
                    </button>
                  </Magnetic>
                </div>
                <p className="text-[12px] pt-2" style={{ color: 'var(--award-cirrus-3)' }} dangerouslySetInnerHTML={{ __html: t('donate.multipleContributionsNote') }} />
              </div>
            </Reveal>

            <Reveal delay={4}>
              <p className="text-center mt-8 text-[13px]" style={{ color: 'var(--award-cirrus-3)' }}>
                {t('donate.donateRunsVia')}{' '}
                <strong style={{ color: 'var(--award-cirrus)' }}>{paypalCfg.donateEmail}</strong>.
              </p>
            </Reveal>
          </div>
        </section>

        {/* GOLDENER RING — Dankeschön das man sieht.
            R91.8.3 (2026-05-19): Stephan-Spec — der "Super."-Block wird zu
            5 Tier-spezifischen Erklär-Reihen umgebaut. Links das Ring-Visual
            mit Animation, rechts Beschreibung + direkter PayPal-Klick.
            Die zentrale Live-Vorschau bleibt als Hero oben. */}
        <section className="py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">

            {/* Hero: Live-Vorschau + Super.-Einleitung */}
            <div className="grid grid-cols-12 gap-6 md:gap-12 items-center mb-16">
              <div className="col-span-12 md:col-span-5">
                <Reveal>
                  <RingPreview />
                </Reveal>
              </div>
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>
                  <div className="award-eyebrow mb-3">{t('donate.ringHeroEyebrow')}</div>
                  <h2 className="award-h-lg mb-4">{t('donate.ringHeroTitle')}</h2>
                </Reveal>
                <Reveal delay={2}>
                  <p className="award-body-lead mb-3" dangerouslySetInnerHTML={{ __html: t('donate.ringHeroBody') }} />
                  <p className="text-sm" style={{ color: 'var(--award-cirrus-3)' }}>
                    {t('donate.ringHeroContact')}{' '}
                    <a href={`mailto:${paypalCfg.donateEmail}?subject=FGL%20F%C3%B6rderbeitrag`} className="underline">{paypalCfg.donateEmail}</a>.
                  </p>
                </Reveal>
              </div>
            </div>

            {/* Tier-Explainer-Reihen entfernt (R100.51 2026-05-27): die
                rotierende RingPreview oben zeigt jetzt schon Tier-Wechsel
                inkl. Animations-Text + Beschreibung. Die 5 großen Auswahl-
                Cards sitzen weiter oben unter #auswahl. */}

            {/* Anker-Pille zurück hoch zu den 5 Karten */}
            <Reveal delay={1}>
              <div className="text-center mt-12">
                <a
                  href="#auswahl"
                  className="wertung-tab-pill wertung-tab-pill-lg inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase text-sm tracking-widest"
                  data-state="active"
                >
                  <span aria-hidden>↑</span> {t('donate.backToSelection')}
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* TRANSPARENZ */}
        <section
          className="py-20 md:py-28 border-t"
          style={{ borderColor: 'var(--award-line)', background: 'var(--award-ink-2)' }}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="text-center mb-12">
                <div className="award-eyebrow mb-2">{t('donate.transparencyEyebrow')}</div>
                <h2 className="award-h-lg">{t('donate.transparencyTitle')}</h2>
                <p className="award-body-lead mx-auto mt-4">
                  {t('donate.transparencyLead')}
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {TRANSPARENZ.map((item, i) => (
                <Reveal key={i} delay={(i % 4) as 0 | 1 | 2 | 3}>
                  <div
                    className="h-full p-6 transition-all duration-300 hover:-translate-y-1"
                    style={{
                      borderRadius: '18px',
                      background: 'var(--award-ink-3)',
                      border: '1px solid var(--award-line)',
                    }}
                  >
                    <div className="flex items-baseline gap-3 mb-4">
                      <span
                        className="font-mono text-[11px] font-bold tracking-[0.18em]"
                        style={{ color: 'var(--award-gold)' }}
                      >
                        {item.code}
                      </span>
                      <span className="h-px flex-1" style={{ background: 'var(--award-line)' }} />
                    </div>
                    <div
                      className="inline-flex w-12 h-12 rounded-xl items-center justify-center mb-4"
                      style={{ background: 'var(--award-gold-soft)' }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: 'var(--award-gold)' }} />
                    </div>
                    <h3
                      className="font-bold text-base mb-2"
                      style={{ fontFamily: 'var(--font-display)', color: 'var(--award-cirrus)' }}
                    >
                      {t(item.titleKey)}
                    </h3>
                    <p className="text-[13px] leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                      {t(item.descKey)}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* RECHTLICHER HINWEIS — Steuerlich relevanter Disclaimer.
            R91.1 follow-up (2026-05-19): Stephans Original-Wording als Lead
            (Sie-Form bewusst beibehalten — legal-formaler Ton), plus
            UStG-Klarstellung als Folge-Absatz. */}
        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <Reveal>
              <div
                className="p-6 md:p-8"
                style={{
                  borderRadius: '18px',
                  background: 'var(--award-ink-3)',
                  border: '1px solid var(--award-line)',
                }}
              >
                <div className="award-eyebrow mb-3">{t('donate.legalEyebrowText')}</div>
                <h3 className="award-h-md mb-4">{t('donate.legalTitleText')}</h3>
                <div
                  className="space-y-3 text-[14px] leading-relaxed"
                  style={{ color: 'var(--award-cirrus-2)' }}
                >
                  <p dangerouslySetInnerHTML={{ __html: t('donate.legalP1') }} />
                  <p dangerouslySetInnerHTML={{ __html: t('donate.legalP2') }} />
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* CLOSING */}
        <section
          className="py-20 md:py-28 border-t relative overflow-hidden"
          style={{ borderColor: 'var(--award-line)', background: 'var(--award-ink-2)' }}
        >
          <div className="award-thermal-dot" style={{ left: '15%', top: '30%' }} />
          <div className="award-thermal-dot" style={{ left: '85%', top: '60%', animationDelay: '1.5s' }} />
          <div className="max-w-[1100px] mx-auto px-6 text-center relative z-10">
            <Reveal>
              <div className="award-eyebrow mb-4">{t('donate.closingEyebrow')}</div>
              <h3 className="award-h-lg mb-8">
                {t('donate.closingTitle1')} <span className="award-grad-text">{t('donate.closingTitleAccent')}</span>
              </h3>
            </Reveal>
            <Reveal delay={1}>
              <div className="flex gap-4 justify-center flex-wrap">
                <Magnetic>
                  <button
                    type="button"
                    onClick={() => void startDonate(50, 'FOERDERER')}
                    disabled={startingDonate}
                    className="award-btn award-btn-gold"
                    style={{ opacity: startingDonate ? 0.5 : 1, cursor: startingDonate ? 'wait' : 'pointer' }}
                  >
                    {t('donate.closingCta50')} <span aria-hidden>→</span>
                  </button>
                </Magnetic>
                <Magnetic>
                  <Link href="/" className="award-btn award-btn-ghost">
                    {t('donate.closingCtaHome')}
                  </Link>
                </Magnetic>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </LenisProvider>
  )
}

/**
 * RingPreview (R91.1 follow-up, 2026-05-19)
 *
 * Live-Preview links neben dem Ring-Erklärtext:
 *  - eingeloggt: zeigt eigenen Avatar mit Förderer-Ring drumherum
 *  - ausgeloggt: Karussell durch die 5 illustrierten Beispiel-Modellpiloten
 *
 * Damit sieht der Besucher sofort wie sein eigener Ring aussehen wird,
 * statt nur abstrakt davon zu lesen.
 */
function RingPreview() {
  const { t } = useTranslation()
  const { data: session, status } = useSession()
  const [idx, setIdx] = useState(0)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)

  // R100.51 (2026-05-27): Karussell rotiert durch ALLE 5 Tiers — Avatar
  // wechselt Tier UND Beschreibung. Vorher fix FOERDERER + 5 Beispiel-
  // Pilotengesichter. Eingeloggte sehen ihren eigenen Avatar mit
  // wechselndem Tier-Showcase darunter.
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % TIER_EXPLAINERS.length), 3200)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (status !== 'authenticated') {
      setAvatarUrl(null)
      return
    }
    setAvatarUrl(session?.user?.image ?? null)
    fetch('/api/pilot-profile')
      .then((r) => r.json())
      .then((j) => {
        const url = j?.data?.avatarMediaId as string | null | undefined
        if (url) setAvatarUrl(url)
      })
      .catch(() => {})
  }, [status, session?.user?.image])

  const isLoggedIn = !!session?.user
  const tier = TIER_EXPLAINERS[idx]
  const isTrinkgeld = tier.kindKey === 'donate.tierKindTrinkgeld'

  return (
    <div className="flex flex-col items-center text-center">
      <DonorAvatar
        src={isLoggedIn ? avatarUrl ?? undefined : undefined}
        name={isLoggedIn ? (session.user?.name || 'Du') : 'FGL'}
        donorTier={tier.tier}
        size={156}
        showBadge
        daysLeft={tier.daysLeft}
        exampleAvatarIndex={isLoggedIn ? undefined : tier.pilotIndex}
      />

      {/* Tier-Pille + Stufen-Name */}
      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
          style={{
            background: isTrinkgeld ? 'rgba(96, 165, 250, 0.12)' : 'rgba(242, 201, 78, 0.14)',
            color: isTrinkgeld ? '#60a5fa' : '#f2c94e',
            border: `1px solid ${isTrinkgeld ? 'rgba(96, 165, 250, 0.30)' : 'rgba(242, 201, 78, 0.35)'}`,
            letterSpacing: '0.10em',
          }}
        >
          {t(tier.kindKey)}
        </span>
        <h3
          className="text-base font-black uppercase"
          style={{
            color: 'var(--award-gold)',
            letterSpacing: '0.06em',
            fontFamily: 'var(--font-display)',
          }}
        >
          {t(tier.labelKey)}
        </h3>
        <span
          className="text-base font-black tabular-nums"
          style={{ color: 'var(--award-cirrus)', fontFamily: 'var(--font-display)' }}
        >
          · {tier.amount}
        </span>
      </div>

      {/* Animations-Text + Beschreibung — wechseln mit dem Tier */}
      <p
        className="mt-3 text-[13px] leading-relaxed max-w-md"
        style={{ color: 'var(--award-cirrus)' }}
      >
        {t(tier.animationKey)}
      </p>
      <p
        className="mt-1.5 text-[11px] leading-relaxed max-w-md"
        style={{ color: 'var(--award-cirrus-3)' }}
      >
        {t(tier.descriptionKey)}
      </p>

      {/* Caption darunter — Live/Example */}
      <div
        className="mt-4 text-[10px] uppercase tracking-[0.18em] font-bold"
        style={{ color: 'var(--award-cirrus-3)', fontFamily: 'var(--font-display)' }}
      >
        {isLoggedIn ? t('donate.ringPreviewLiveLabel') : t('donate.ringPreviewExampleLabel')}
      </div>

      {/* Dot-Indicator: zeigt aktuellen Tier-Index */}
      <div className="mt-3 flex gap-1.5 items-center" aria-hidden="true">
        {TIER_EXPLAINERS.map((_, i) => (
          <span
            key={i}
            className="block rounded-full transition-all"
            style={{
              width: i === idx ? '20px' : '6px',
              height: '6px',
              background: i === idx ? 'var(--award-gold)' : 'var(--award-line-hi)',
            }}
          />
        ))}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────
 * R91.8.3 — Tier-Explainer-Reihen
 * Eine pro Stufe mit:
 *  - Links: Live-Ring-Visual (Avatar mit Tier-spezifischer Animation)
 *  - Mitte: Tier-Name + Animations-Beschreibung + Kurztext
 *  - Rechts: Beitrag + Förderbeitrag/Trinkgeld-Geben-Pill
 *  - Click auf die ganze Karte → PayPal in neuem Tab
 * ───────────────────────────────────────────────────────────────────── */

interface TierExplainer {
  tier: 'SUPPORTER' | 'UNTERSTUETZER' | 'FOERDERER' | 'SUPPORTER_MONTHLY' | 'GOENNER_MONTHLY'
  labelKey: string
  amount: string
  amountSubKey: string
  kindKey: 'donate.tierKindTrinkgeld' | 'donate.tierKindFoerder'
  animationKey: string
  descriptionKey: string
  ctaKey: 'donate.tierCtaTrinkgeld' | 'donate.tierCtaFoerder'
  pilotIndex: number
  daysLeft: number | null
  paypalAmount: number
  isSubscription: boolean
}

const TIER_EXPLAINERS: TierExplainer[] = [
  {
    tier: 'SUPPORTER',
    labelKey: 'donate.tierSupporterLabel',
    amount: '10 €',
    amountSubKey: 'donate.tierAmountOnce',
    kindKey: 'donate.tierKindTrinkgeld',
    animationKey: 'donate.tierSupporterAnimation',
    descriptionKey: 'donate.tierSupporterDescription',
    ctaKey: 'donate.tierCtaTrinkgeld',
    pilotIndex: 0,
    daysLeft: 365,
    paypalAmount: 10,
    isSubscription: false,
  },
  {
    tier: 'UNTERSTUETZER',
    labelKey: 'donate.tierUnterstuetzerLabel',
    amount: '20 €',
    amountSubKey: 'donate.tierAmountOnce',
    kindKey: 'donate.tierKindTrinkgeld',
    animationKey: 'donate.tierUnterstuetzerAnimation',
    descriptionKey: 'donate.tierUnterstuetzerDescription',
    ctaKey: 'donate.tierCtaTrinkgeld',
    pilotIndex: 1,
    daysLeft: 365,
    paypalAmount: 20,
    isSubscription: false,
  },
  {
    tier: 'FOERDERER',
    labelKey: 'donate.tierFoerdererLabel',
    amount: '50 €',
    amountSubKey: 'donate.tierAmountOnce',
    kindKey: 'donate.tierKindFoerder',
    animationKey: 'donate.tierFoerdererAnimation',
    descriptionKey: 'donate.tierFoerdererDescription',
    ctaKey: 'donate.tierCtaFoerder',
    pilotIndex: 2,
    daysLeft: 365,
    paypalAmount: 50,
    isSubscription: false,
  },
  {
    tier: 'SUPPORTER_MONTHLY',
    labelKey: 'donate.tierSupporterMonthlyLabel',
    amount: '8 €',
    amountSubKey: 'donate.tierAmountMonthly',
    kindKey: 'donate.tierKindFoerder',
    animationKey: 'donate.tierSupporterMonthlyAnimation',
    descriptionKey: 'donate.tierSupporterMonthlyDescription',
    ctaKey: 'donate.tierCtaFoerder',
    pilotIndex: 3,
    daysLeft: null,
    paypalAmount: 8,
    isSubscription: true,
  },
  {
    tier: 'GOENNER_MONTHLY',
    labelKey: 'donate.tierGoennerMonthlyLabel',
    amount: '12 €',
    amountSubKey: 'donate.tierAmountMonthly',
    kindKey: 'donate.tierKindFoerder',
    animationKey: 'donate.tierGoennerMonthlyAnimation',
    descriptionKey: 'donate.tierGoennerMonthlyDescription',
    ctaKey: 'donate.tierCtaFoerder',
    pilotIndex: 4,
    daysLeft: null,
    paypalAmount: 12,
    isSubscription: true,
  },
]

// TierExplainerRow entfernt (R100.51 2026-05-27): obsolet nach Auflösung
// der Bottom-Tier-Liste. RingPreview zeigt jetzt rotierend alle Tiers an.
